import React, { useState, useEffect, useCallback, useRef } from 'react';
import socketService from './socketService';
import api from './api';
import ChatWidget from './ChatWidget';
import {
  getCameraPreference,
  listVideoInputDevices,
  precheckPreferredCamera,
  probeCameraDevice,
  saveCameraPreference,
  clearCameraPreference,
} from './utils/cameraPreference';
import './styles/online.css';

/**
 * OnlineLobby - Sala de espera online
 * 
 * Muestra jugadores conectados, permite enviar/recibir retos,
 * y gestiona el flujo hasta que se acepta un reto y se entra a partida.
 */
const OnlineLobby = ({ onBack, onGameStart, onGoToMetrics }) => {
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting | connected | error
  const [players, setPlayers] = useState([]);
  const [myStatus, setMyStatus] = useState('available'); // available | challenging | challenged
  const currentUser = api.getCurrentUser();
  const [myPlayerData, setMyPlayerData] = useState(null);

  // Reto activo (enviado o recibido)
  const [activeChallenge, setActiveChallenge] = useState(null);
  // Modal de configuración de reto
  const [challengeModal, setChallengeModal] = useState(null); // { targetPlayer }
  const [challengeConfig, setChallengeConfig] = useState({ gameMode: 501, legsToWin: 3 });
  // Timer de expiración
  const [challengeTimer, setChallengeTimer] = useState(null);
  const timerRef = useRef(null);
  // Ref para evitar desconectar socket al navegar al juego
  const transitioningToGameRef = useRef(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraModalRequired, setCameraModalRequired] = useState(false);
  const [cameraModalMessage, setCameraModalMessage] = useState('');
  const [cameraModalLoading, setCameraModalLoading] = useState(false);
  const [cameraModalSaving, setCameraModalSaving] = useState(false);
  const [cameraModalError, setCameraModalError] = useState('');
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [cameraConfigured, setCameraConfigured] = useState(() => Boolean(getCameraPreference()?.deviceId));
  const [cameraTutorial, setCameraTutorial] = useState(null); // { message }
  const cameraPreviewRef = useRef(null);
  const cameraPreviewStreamRef = useRef(null);

  // ─── Conexión y listeners ───
  useEffect(() => {
    let isMounted = true;

    const handleConnect = () => {
      setConnectionStatus('connected');
      // Resetear estado de reto tras reconexión (el servidor ya canceló retos pendientes)
      setActiveChallenge(null);
      setMyStatus('available');
    };
    const handleDisconnect = () => setConnectionStatus('error');
    const handleConnectError = (err) => {
      const errorMessage = err?.message || 'UNKNOWN_SOCKET_ERROR';
      console.error('Error conexión lobby:', errorMessage);
      if (isMounted) {
        setConnectionStatus('error');
      }
    };

    // Registrar via socketService.on() para que sobrevivan a reconnect()
    const cleanupConnect = socketService.on('connect', handleConnect);
    const cleanupDisconnect = socketService.on('disconnect', handleDisconnect);
    const cleanupError = socketService.on('connect_error', handleConnectError);

    // Si ya está conectado al montar
    if (socketService.isConnected) {
      setConnectionStatus('connected');
    }

    const initializeSocket = async () => {
      const ready = await api.ensureValidAccessToken();

      if (!ready) {
        if (isMounted) {
          setConnectionStatus('error');
        }
        return;
      }

      const socket = socketService.connect();

      if (!socket && isMounted) {
        setConnectionStatus('error');
      }
    };

    void initializeSocket();

    // Limpiar al salir
    return () => {
      isMounted = false;
      cleanupConnect();
      cleanupDisconnect();
      cleanupError();
      if (timerRef.current) clearInterval(timerRef.current);
      // Solo desconectar si NO estamos navegando al juego
      if (!transitioningToGameRef.current) {
        socketService.disconnect();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Eventos del lobby ───
  useEffect(() => {
    const handlePlayers = (playerList) => {
      // Separar mi card y los demás
      const me = playerList.find(p => p.id === currentUser?._id);
      if (me) setMyPlayerData(me);
      const others = playerList.filter(p => p.id !== currentUser?._id);
      setPlayers(others);
    };

    const handleChallengeReceived = (data) => {
      setMyStatus('challenged');
      setActiveChallenge({
        type: 'received',
        challenger: data.challenger,
        config: data.config,
        expiresAt: data.expiresAt
      });
      startTimer(data.expiresAt);
    };

    const handleChallengeSent = () => {
      setMyStatus('challenging');
    };

    const handleChallengeAccepted = (data) => {
      clearTimer();
      setActiveChallenge(null);
      setMyStatus('available');
      // Marcar que vamos al juego → NO desconectar socket en cleanup
      transitioningToGameRef.current = true;
      // Empezar partida — data ya ES el gameSetup (roomId, config, player1, player2)
      if (onGameStart) {
        onGameStart(data);
      }
    };

    const handleChallengeRejected = (data) => {
      clearTimer();
      setActiveChallenge(null);
      setMyStatus('available');
    };

    const handleChallengeCancelled = () => {
      clearTimer();
      setActiveChallenge(null);
      setMyStatus('available');
    };

    const handleChallengeExpired = () => {
      clearTimer();
      setActiveChallenge(null);
      setMyStatus('available');
    };

    const handleChallengeError = (data) => {
      clearTimer();
      setActiveChallenge(null);
      setMyStatus('available');
      console.error('Error de reto:', data.message);
    };

    const cleanups = [
      socketService.on('lobby:players', handlePlayers),
      socketService.on('challenge:received', handleChallengeReceived),
      socketService.on('challenge:sent', handleChallengeSent),
      socketService.on('challenge:accepted', handleChallengeAccepted),
      socketService.on('challenge:rejected', handleChallengeRejected),
      socketService.on('challenge:cancelled', handleChallengeCancelled),
      socketService.on('challenge:expired', handleChallengeExpired),
      socketService.on('challenge:error', handleChallengeError),
    ];

    // Si volvemos del juego con socket ya conectado, pedir lista fresca
    if (socketService.isConnected) {
      socketService.emit('lobby:refresh');
    }

    return () => cleanups.forEach(cleanup => cleanup());
  }, [currentUser, onGameStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Timer de reto ───
  const startTimer = useCallback((expiresAt) => {
    clearTimer();
    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setChallengeTimer(remaining);
      if (remaining <= 0) clearTimer();
    };
    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setChallengeTimer(null);
  }, []);

  const stopCameraPreview = useCallback(() => {
    if (cameraPreviewStreamRef.current) {
      cameraPreviewStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraPreviewStreamRef.current = null;
    }
    if (cameraPreviewRef.current) {
      cameraPreviewRef.current.srcObject = null;
    }
  }, []);

  const startCameraPreview = useCallback(async (deviceId) => {
    stopCameraPreview();
    const stream = await navigator.mediaDevices.getUserMedia({
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
      audio: false,
    });
    cameraPreviewStreamRef.current = stream;
    if (cameraPreviewRef.current) {
      cameraPreviewRef.current.srcObject = stream;
    }
  }, [stopCameraPreview]);

  const openCameraConfigModal = useCallback(async ({ required, message = '', action = null } = {}) => {
    setCameraModalOpen(true);
    setCameraModalRequired(Boolean(required));
    setCameraModalMessage(message);
    setCameraModalLoading(true);
    setCameraModalError('');
    setPendingAction(action);

    try {
      const devices = await listVideoInputDevices();
      setCameraDevices(devices);

      if (devices.length === 0) {
        setSelectedCameraId('');
        setCameraModalError('No se detectó ninguna cámara disponible.');
        return;
      }

      const preferred = getCameraPreference();
      const preferredExists = preferred?.deviceId && devices.some((d) => d.deviceId === preferred.deviceId);
      const initialId = preferredExists ? preferred.deviceId : devices[0].deviceId;
      setSelectedCameraId(initialId);
      await startCameraPreview(initialId);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setCameraModalError('Debes permitir acceso a la cámara para configurar el modo online.');
      } else {
        setCameraModalError('No se pudo abrir la cámara. Comprueba permisos y reintenta.');
      }
    } finally {
      setCameraModalLoading(false);
    }
  }, [startCameraPreview]);

  const closeCameraModal = useCallback(() => {
    stopCameraPreview();
    setCameraModalOpen(false);
    setCameraModalRequired(false);
    setCameraModalMessage('');
    setCameraModalError('');
    setPendingAction(null);
  }, [stopCameraPreview]);

  const executeLobbyAction = useCallback((action) => {
    if (!action?.type) return;

    if (action.type === 'send-challenge') {
      socketService.emit('challenge:send', {
        targetUserId: action.payload.targetUserId,
        config: action.payload.config,
      });
      setActiveChallenge({
        type: 'sent',
        target: action.payload.target,
        config: action.payload.config,
      });
      return;
    }

    if (action.type === 'accept-challenge') {
      socketService.emit('challenge:accept');
    }
  }, []);

  const ensureCameraReadyOrConfigure = useCallback(async (action) => {
    const precheck = await precheckPreferredCamera();
    if (precheck.ok) {
      executeLobbyAction(action);
      return;
    }

    // Fallback de compatibilidad: si el navegador no soporta precheck,
    // no bloqueamos el reto y dejamos que el setup de cámara gestione el error.
    if (precheck.reason === 'not_supported') {
      executeLobbyAction(action);
      return;
    }

    setCameraTutorial({ message: precheck.message });
  }, [executeLobbyAction]);

  useEffect(() => {
    return () => {
      stopCameraPreview();
    };
  }, [stopCameraPreview]);

  useEffect(() => {
    if (!cameraModalOpen || !selectedCameraId) return;

    let cancelled = false;
    const run = async () => {
      try {
        await startCameraPreview(selectedCameraId);
        if (!cancelled) setCameraModalError('');
      } catch (err) {
        if (cancelled) return;
        setCameraModalError('No se pudo abrir la cámara seleccionada.');
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [cameraModalOpen, selectedCameraId, startCameraPreview]);

  // ─── Acciones ───
  const openChallengeModal = (player) => {
    setChallengeModal({ targetPlayer: player });
    setChallengeConfig({ gameMode: 501, legsToWin: 3 });
  };

  const sendChallenge = async () => {
    if (!challengeModal) return;
    const action = {
      type: 'send-challenge',
      payload: {
        targetUserId: challengeModal.targetPlayer.id,
        target: challengeModal.targetPlayer,
        config: challengeConfig,
      },
    };
    setChallengeModal(null);
    await ensureCameraReadyOrConfigure(action);
  };

  const acceptChallenge = async () => {
    await ensureCameraReadyOrConfigure({ type: 'accept-challenge' });
  };

  const rejectChallenge = () => {
    socketService.emit('challenge:reject');
  };

  const cancelChallenge = () => {
    socketService.emit('challenge:cancel');
    setActiveChallenge(null);
    setMyStatus('available');
    clearTimer();
  };

  const handleBack = () => {
    if (activeChallenge?.type === 'sent') {
      cancelChallenge();
    }
    onBack();
  };

  const handleSaveCameraConfig = async () => {
    if (!selectedCameraId) {
      setCameraModalError('Selecciona una cámara para continuar.');
      return;
    }

    setCameraModalSaving(true);
    setCameraModalError('');
    try {
      const validated = await probeCameraDevice(selectedCameraId);
      if (!validated.deviceId) {
        throw new Error('No se pudo validar la cámara seleccionada');
      }

      saveCameraPreference({ deviceId: validated.deviceId, label: validated.label });
      setCameraConfigured(true);

      const actionToRun = pendingAction;
      closeCameraModal();
      executeLobbyAction(actionToRun);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setCameraModalError('Permiso de cámara denegado. Debes permitirlo para jugar online.');
      } else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        clearCameraPreference();
        setCameraConfigured(false);
        setCameraModalError('Esa cámara ya no está disponible. Elige otra.');
      } else {
        setCameraModalError('No se pudo guardar la cámara. Inténtalo de nuevo.');
      }
    } finally {
      setCameraModalSaving(false);
    }
  };

  // ─── Render ───
  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return '🟢';
      case 'challenging': case 'challenged': return '🟡';
      case 'in-game': return '🔴';
      default: return '⚪';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'challenging': case 'challenged': return 'En reto';
      case 'in-game': return 'En partida';
      default: return 'Desconocido';
    }
  };

  const getSkillTier = (average) => {
    const value = Number(average);
    if (!Number.isFinite(value)) {
      return { key: 'unranked', label: 'Sin tier' };
    }
    if (value >= 80) {
      return { key: 'diamond', label: 'Diamante' };
    }
    if (value >= 60) {
      return { key: 'gold', label: 'Oro' };
    }
    if (value >= 40) {
      return { key: 'silver', label: 'Plata' };
    }
    return { key: 'bronze', label: 'Bronce' };
  };

  const renderSkillIcon = (tierKey) => {
    if (tierKey === 'diamond') {
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="online-skill-icon-svg">
          <path d="M5 9L9 4h6l4 5-7 10L5 9z" />
          <path d="M9 4l3 5 3-5" />
          <path d="M5 9h14" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="online-skill-icon-svg">
        <circle cx="12" cy="11" r="6.5" />
        <path d="M9 17l-2.4 4 3.6-1.6L12 23l1.8-3.6 3.6 1.6-2.4-4" />
      </svg>
    );
  };

  const getTimeAgo = (timestamp) => {
    const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    const seconds = Math.floor((Date.now() - time) / 1000);
    if (isNaN(seconds) || seconds < 60) return 'ahora';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h`;
  };

  const mySkillTier = getSkillTier(myPlayerData?.promedioOnline);

  return (
    <div className="dart-league-dashboard training-menu-container">

        {/* Header */}
        <header className="training-menu-header online-lobby-header">
          <div className="training-header-top">
            <button className="training-back-btn" onClick={handleBack}>
              ← Volver
            </button>
          </div>
          <div className="training-header-icon">
            <img src="/Logo26.png" alt="501 Dart League" className="training-header-logo" />
          </div>
          <div className={`online-lobby-live-badge ${connectionStatus}`}>
            <span className="online-lobby-status-dot"></span>
            {connectionStatus === 'connecting' && 'Conectando...'}
            {connectionStatus === 'connected' && 'En línea'}
            {connectionStatus === 'error' && 'Sin conexión'}
          </div>
          <h1 className="online-lobby-title">Sala Online</h1>
          <div className="online-lobby-header-actions">
            {currentUser?.username?.toLowerCase() === 'juanmi' && onGoToMetrics && (
              <button className="online-lobby-retry" onClick={onGoToMetrics}>
                Métricas online
              </button>
            )}
            <button
              className="online-lobby-retry"
              onClick={() => openCameraConfigModal({ required: false, message: 'Configura o revisa tu cámara para el modo online.' })}
            >
              🎥 Configurar cámara
            </button>
            <span className="online-lobby-camera-status">
              {cameraConfigured ? '✅ Cámara configurada' : '⚠️ Cámara pendiente'}
            </span>
          </div>
          <p className="online-lobby-subtitle">
            {players.length === 0
              ? 'Esperando rivales...'
              : `${players.length} jugador${players.length !== 1 ? 'es' : ''} en la sala`}
          </p>

          {connectionStatus === 'connected' && !cameraConfigured && (
            <div className="online-lobby-empty-hint online-lobby-camera-hint">
              <strong>Antes de invitar:</strong> pulsa <strong>🎥 Configurar cámara</strong>, elige la cámara que apunta a la diana y guárdala.
              <button
                className="online-lobby-retry online-lobby-camera-hint-btn"
                onClick={() => openCameraConfigModal({ required: false, message: 'Configura tu cámara una vez y quedará guardada para las próximas partidas.' })}
              >
                Configurar ya
              </button>
            </div>
          )}
        </header>

        {/* Banner de reto recibido */}
        {activeChallenge?.type === 'received' && (
          <div className="online-challenge-banner received">
            <div className="online-challenge-banner-info">
              <span className="online-challenge-banner-icon">⚔️</span>
              <div>
                <strong>{activeChallenge.challenger.username}</strong> te reta
                <span className="online-challenge-banner-config">
                  {activeChallenge.config.gameMode} — Primero a {activeChallenge.config.legsToWin}
                </span>
              </div>
              {challengeTimer !== null && (
                <span className="online-challenge-timer">{challengeTimer}s</span>
              )}
            </div>
            <div className="online-challenge-banner-actions">
              <button className="online-challenge-btn accept" onClick={acceptChallenge}>
                ✓ Aceptar
              </button>
              <button className="online-challenge-btn reject" onClick={rejectChallenge}>
                ✗ Rechazar
              </button>
            </div>
          </div>
        )}

        {/* Banner de reto enviado */}
        {activeChallenge?.type === 'sent' && (
          <div className="online-challenge-banner sent">
            <div className="online-challenge-banner-info">
              <span className="online-challenge-banner-icon">⏳</span>
              <div>
                Esperando respuesta de <strong>{activeChallenge.target.username}</strong>
                <span className="online-challenge-banner-config">
                  {activeChallenge.config.gameMode} — Primero a {activeChallenge.config.legsToWin}
                </span>
              </div>
            </div>
            <button className="online-challenge-btn cancel" onClick={cancelChallenge}>
              Cancelar
            </button>
          </div>
        )}

        {/* Mi card - cómo me ven los demás */}
        {connectionStatus === 'connected' && myPlayerData && (
          <section className="online-lobby-my-card-section">
            <span className="online-lobby-my-card-label">Así te ven los demás</span>
            <div className={`online-lobby-player-card my-card available skill-${mySkillTier.key}`}>
              <div className="online-player-avatar-wrapper">
                <div className="online-player-status-ring"></div>
                <div className="online-lobby-player-avatar">
                  {myPlayerData.profilePhoto
                    ? <img src={myPlayerData.profilePhoto} alt={myPlayerData.username} />
                    : <span>{myPlayerData.username.charAt(0).toUpperCase()}</span>
                  }
                </div>
              </div>
              <div className="online-lobby-player-info">
                <span className="online-lobby-player-name">
                  {myPlayerData.username}
                  <span className={`online-skill-badge ${mySkillTier.key}`} title={`Tier por media online: ${mySkillTier.label}`}>
                    <span className="online-skill-icon">{renderSkillIcon(mySkillTier.key)}</span>
                    <span className="online-skill-label">{mySkillTier.label}</span>
                  </span>
                </span>
                {(myPlayerData.promedioOnline != null || myPlayerData.winRateOnline != null) && (
                  <div className="online-my-card-stats">
                    {myPlayerData.promedioOnline != null && <span className="online-my-card-stat" title="Media online">Avrg {Number(myPlayerData.promedioOnline).toFixed(1)}</span>}
                    {myPlayerData.winRateOnline != null && <span className="online-my-card-stat" title="Victorias online">🏆 {myPlayerData.winRateOnline}%</span>}
                  </div>
                )}
                <div className="online-player-meta">
                  <span className="online-lobby-player-status">
                    🟢 Disponible
                  </span>
                </div>
              </div>
            </div>
          </section>
        )}

        <div className="online-lobby-layout">
          <div className="online-lobby-players-column">
            {/* Lista de jugadores */}
            <section className="online-lobby-players">
              {connectionStatus === 'connecting' && (
                <div className="online-lobby-empty">
                  <div className="online-lobby-empty-icon">⏳</div>
                  <p>Conectando al servidor...</p>
                </div>
              )}

              {connectionStatus === 'error' && (
                <div className="online-lobby-empty">
                  <div className="online-lobby-empty-icon">❌</div>
                  <p>No se pudo conectar al servidor</p>
                  <button className="online-lobby-retry" onClick={() => {
                    setConnectionStatus('connecting');
                    socketService.reconnect();
                  }}>
                    Reintentar
                  </button>
                </div>
              )}

              {connectionStatus === 'connected' && players.length === 0 && (
                <div className="online-lobby-empty">
                  <div className="online-lobby-empty-icon">😕</div>
                  <p>No hay otros jugadores en la sala ahora mismo.</p>
                  <span className="online-lobby-empty-hint">
                    ¡Comparte la app! Que se registren con tu nombre de referido y recibirás recompensas exclusivas.
                  </span>
                </div>
              )}

              {connectionStatus === 'connected' && players.map(player => {
                const skillTier = getSkillTier(player.promedioOnline);
                return (
                <div key={player.id} className={`online-lobby-player-card ${player.status} skill-${skillTier.key}`}>
                  <div className="online-player-avatar-wrapper">
                    <div className="online-player-status-ring"></div>
                    <div className="online-lobby-player-avatar">
                      {player.profilePhoto
                        ? <img src={player.profilePhoto} alt={player.username} />
                        : <span>{player.username.charAt(0).toUpperCase()}</span>
                      }
                    </div>
                  </div>
                  <div className="online-lobby-player-info">
                    <span className="online-lobby-player-name">
                      {player.username}
                      <span className={`online-skill-badge ${skillTier.key}`} title={`Tier por media online: ${skillTier.label}`}>
                        <span className="online-skill-icon">{renderSkillIcon(skillTier.key)}</span>
                        <span className="online-skill-label">{skillTier.label}</span>
                      </span>
                    </span>
                    {(player.promedioOnline != null || player.winRateOnline != null) && (
                      <div className="online-my-card-stats">
                        {player.promedioOnline != null && <span className="online-my-card-stat" title="Media online">Avrg {Number(player.promedioOnline).toFixed(1)}</span>}
                        {player.winRateOnline != null && <span className="online-my-card-stat" title="Victorias online">🏆 {player.winRateOnline}%</span>}
                      </div>
                    )}
                    <div className="online-player-meta">
                      <span className="online-lobby-player-status">
                        {getStatusIcon(player.status)} {getStatusText(player.status)}
                      </span>
                      {player.connectedAt && (
                        <span className="online-player-time">
                          ⏱ {getTimeAgo(player.connectedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="online-lobby-player-action">
                    {player.status === 'available' && myStatus === 'available' && (
                      <button 
                        className="online-lobby-challenge-btn"
                        onClick={() => openChallengeModal(player)}
                      >
                        ⚔️ Retar
                      </button>
                    )}
                    {player.status === 'in-game' && (
                      <span className="online-lobby-player-busy">En partida</span>
                    )}
                  </div>
                </div>
              )})}
            </section>
          </div>

          <aside className="online-lobby-chat">
            <ChatWidget
              scope="lobby"
              currentUserId={currentUser?._id}
              title="Chat lobby"
              quickMessages={['hola', 'alguien juega?']}
              dockedOnDesktop={true}
            />
          </aside>
        </div>

      {/* Modal de configuración de reto */}
      {challengeModal && (
        <div className="online-modal-overlay" onClick={() => setChallengeModal(null)}>
          <div className="online-modal" onClick={e => e.stopPropagation()}>
            <div className="online-modal-header">
              <h2>⚔️ Retar a {challengeModal.targetPlayer.username}</h2>
              <button className="online-modal-close" onClick={() => setChallengeModal(null)}>✕</button>
            </div>
            <div className="online-modal-body">
              {/* Modo de juego */}
              <div className="online-modal-field">
                <label>Modalidad</label>
                <div className="online-modal-options">
                  {[301, 501, 701].map(mode => (
                    <button
                      key={mode}
                      className={`online-modal-option ${challengeConfig.gameMode === mode ? 'active' : ''}`}
                      onClick={() => setChallengeConfig(c => ({ ...c, gameMode: mode }))}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
              {/* Legs */}
              <div className="online-modal-field">
                <label>Primero a (legs)</label>
                <div className="online-modal-options">
                  {[1, 3, 5, 7].map(legs => (
                    <button
                      key={legs}
                      className={`online-modal-option ${challengeConfig.legsToWin === legs ? 'active' : ''}`}
                      onClick={() => setChallengeConfig(c => ({ ...c, legsToWin: legs }))}
                    >
                      {legs}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="online-modal-footer">
              <button className="online-modal-cancel" onClick={() => setChallengeModal(null)}>
                Cancelar
              </button>
              <button className="online-modal-confirm" onClick={sendChallenge}>
                ⚔️ Enviar Reto
              </button>
            </div>
          </div>
        </div>
      )}

      {cameraModalOpen && (
        <div className="online-modal-overlay" onClick={() => !cameraModalRequired && closeCameraModal()}>
          <div className="online-modal" onClick={(e) => e.stopPropagation()}>
            <div className="online-modal-header">
              <h2>🎥 Configurar cámara online</h2>
              {!cameraModalRequired && (
                <button className="online-modal-close" onClick={closeCameraModal}>✕</button>
              )}
            </div>
            <div className="online-modal-body">
              {cameraModalMessage && (
                <p className="online-modal-description">{cameraModalMessage}</p>
              )}

              {cameraModalLoading ? (
                <p>Cargando cámaras...</p>
              ) : (
                <>
                  <div className="online-modal-field">
                    <label>Cámara</label>
                    <select
                      className="camera-setup-select"
                      value={selectedCameraId}
                      onChange={(e) => setSelectedCameraId(e.target.value)}
                      disabled={cameraModalSaving || cameraDevices.length === 0}
                    >
                      {cameraDevices.length === 0 && <option value="">Sin cámaras detectadas</option>}
                      {cameraDevices.map((cam) => (
                        <option key={cam.deviceId} value={cam.deviceId}>{cam.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="camera-setup-video-wrapper">
                    <video
                      ref={cameraPreviewRef}
                      autoPlay
                      playsInline
                      muted
                      className="camera-setup-video"
                    />
                  </div>
                </>
              )}

              {cameraModalError && (
                <div className="camera-setup-error">
                  <span className="camera-setup-error-icon">⚠️</span>
                  <p>{cameraModalError}</p>
                </div>
              )}
            </div>
            <div className="online-modal-footer">
              {!cameraModalRequired && (
                <button className="online-modal-cancel" onClick={closeCameraModal}>
                  Cerrar
                </button>
              )}
              <button
                className="online-modal-confirm"
                onClick={handleSaveCameraConfig}
                disabled={cameraModalSaving || cameraModalLoading || !selectedCameraId}
              >
                {cameraModalSaving ? 'Guardando...' : cameraModalRequired ? 'Guardar y continuar' : 'Guardar cámara'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cameraTutorial && (
        <div className="online-modal-overlay" onClick={() => setCameraTutorial(null)}>
          <div className="online-modal" onClick={(e) => e.stopPropagation()}>
            <div className="online-modal-header">
              <h2>⚠️ Cámara no configurada</h2>
              <button className="online-modal-close" onClick={() => setCameraTutorial(null)}>✕</button>
            </div>
            <div className="online-modal-body">
              <p className="online-modal-description">{cameraTutorial.message || 'Necesitas configurar la cámara para jugar online.'}</p>
              <div className="online-modal-contact-info no-contact" style={{ marginTop: 12 }}>
                Configúrala desde el <strong>Lobby</strong> usando el botón <strong>🎥 Configurar cámara</strong>.
                <br />
                Después de guardarla, vuelve a intentar invitar o aceptar un reto.
              </div>
            </div>
            <div className="online-modal-footer">
              <button className="online-modal-cancel" onClick={() => setCameraTutorial(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineLobby;
